import { Briefcase, GraduationCap, Sparkles } from 'lucide-react'
import { cn } from '../../../lib/utils'

export type UseCase = 'busy-professional' | 'student-learner' | 'curious-explorer'

const USE_CASES = [
  {
    value: 'busy-professional' as const,
    label: 'I want to turn dead time into discovery time',
    description:
      'Make the most of commutes, workouts, and errands by turning your reading backlog and daily updates into audio you can enjoy on the go.',
    icon: Briefcase,
  },
  {
    value: 'student-learner' as const,
    label: 'I want to turn dry material into dynamic audio',
    description:
      'Make studying easier by transforming dense notes, papers, and course content into engaging audio that fits into your day.',
    icon: GraduationCap,
  },
  {
    value: 'curious-explorer' as const,
    label: 'I want to keep learning without the effort',
    description:
      'Explore the topics you love like psychology, history, and tech through effortless listening, even when you have no time to read.',
    icon: Sparkles,
  },
]

interface UseCaseStepProps {
  value: UseCase | null
  onChange: (value: UseCase) => void
}

export function UseCaseStep({ value, onChange }: UseCaseStepProps) {
  return (
    <div>
      <h2 className="mb-2 text-3xl font-extrabold text-foreground tracking-tight">How will you use your podcast?</h2>
      <p className="mb-8 text-base font-medium text-foreground/60">
        This helps us personalize your setup and suggest the perfect configuration for you.
      </p>

      <div className="space-y-4">
        {USE_CASES.map((useCase) => {
          const Icon = useCase.icon
          const isSelected = value === useCase.value

          return (
            <button
              key={useCase.value}
              onClick={() => onChange(useCase.value)}
              className={cn(
                'group flex w-full cursor-pointer flex-col gap-4 rounded-3xl border p-6 text-left transition-all sm:flex-row sm:items-start',
                isSelected
                  ? 'border-brand-300 bg-brand-50/70 ring-1 ring-brand-300'
                  : 'border-black/10 bg-white hover:border-black/20 hover:bg-black/[0.02]'
              )}
            >
              <div
                className={cn(
                  'flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl transition-colors',
                  isSelected ? 'bg-brand-100 text-brand-700' : 'bg-secondary-100 text-foreground/50 group-hover:bg-secondary-200'
                )}
              >
                <Icon className="h-7 w-7" />
              </div>
              <div className="min-w-0 flex-1 pl-1">
                <div className="mb-2 text-lg font-bold text-foreground">{useCase.label}</div>
                <p className="text-[15px] leading-relaxed text-foreground/60">{useCase.description}</p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export { USE_CASES }
